import { useMemo, useState } from 'react';
import { CaretDown } from '@phosphor-icons/react';
import { toast } from 'sonner';

import { MovePotSheet } from '../../components/pots/MovePotSheet';
import { PotPurposeGroup } from '../../components/pots/PotPurposeGroup';
import { PotSection } from '../../components/pots/PotSection';
import { PotTreasuryCard, PotTreasuryHint } from '../../components/pots/PotTreasuryCard';
import { PotsEmptyState } from '../../components/pots/PotsEmptyState';
import { PotsFooterActions } from '../../components/pots/PotsFooterActions';
import { useAccountsQuery } from '../../features/accounts/queries';
import { useExpenseSheet } from '../../features/expenses/SheetProvider';
import { usePotsQuery } from '../../features/pots/queries';
import { useCurrentSnapshotQuery } from '../../features/snapshots/queries';
import { buildAccountsPageModel } from '../../lib/accountsDisplay';
import {
  groupPotsByPurpose,
  POT_PURPOSE_ORDER,
} from '../../lib/potPurposeUtils';
import { computeTreasuryOverview } from '../../lib/potTreasuryUtils';
import { SectionSkeleton } from '../../components/common/SectionSkeleton';
import { PageContentShell } from '../../components/common/PageContentShell';
import { useQueryShellState } from '../../hooks/useQueryShellState';
import pt from '../../locales/pt';
import { cn } from '../../utils/cn';
import { GuidedFlowReturnBanner } from '../../components/dashboard/GuidedFlowReturnBanner';

export function Pots() {
  const [moveOpen, setMoveOpen] = useState(false);
  const [closedOpen, setClosedOpen] = useState(false);
  const { open: openExpenseSheet } = useExpenseSheet();
  const { data: pots, isPending, isFetching } = usePotsQuery();
  const potsShell = useQueryShellState({ data: pots, isPending, isFetching });
  const potsList = useMemo(() => pots ?? [], [pots]);
  const { data: accounts = [] } = useAccountsQuery();
  const { data: currentSnapshot } = useCurrentSnapshotQuery();

  const activePots = useMemo(
    () => potsList.filter((pot) => !pot.is_closed),
    [potsList],
  );
  const closedPots = useMemo(
    () => potsList.filter((pot) => pot.is_closed),
    [potsList],
  );
  const hasAnyPots = potsList.length > 0;
  const hasActivePots = activePots.length > 0;

  const accountsModel = useMemo(
    () =>
      buildAccountsPageModel({
        accounts,
        accountSnapshots: currentSnapshot?.account_snapshots || [],
      }),
    [accounts, currentSnapshot],
  );
  const treasury = useMemo(
    () =>
      computeTreasuryOverview({
        liquidEur: accountsModel.liquidEur,
        pots: activePots,
      }),
    [accountsModel.liquidEur, activePots],
  );
  const groupedPots = useMemo(
    () => groupPotsByPurpose(activePots),
    [activePots],
  );

  const handleDeposit = () => {
    openExpenseSheet(null);
  };

  const handleMove = () => {
    if (activePots.length < 2) {
      toast.message(pt.pots.move.needTwoPots);
      return;
    }
    setMoveOpen(true);
  };

  return (
    <div className="flex w-full min-w-0 flex-col gap-5 overflow-x-hidden pb-2">
      <GuidedFlowReturnBanner />
      <header className="hidden md:block">
        <h1 className="font-display text-2xl font-semibold text-foreground">
          {pt.pots.title}
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">{pt.pots.subtitle}</p>
      </header>

      <PageContentShell
        shells={[potsShell]}
        fallback={<SectionSkeleton variant="card-grid" />}
      >
        {!hasAnyPots ? (
          <PotsEmptyState />
        ) : !hasActivePots ? (
          <>
            <PotsEmptyState />
            {closedPots.length ? (
              <PotSection title={pt.pots.closedSection} pots={closedPots} />
            ) : null}
          </>
        ) : (
          <>
            <PotTreasuryCard overview={treasury} />
            <PotTreasuryHint overview={treasury} />

            <div>
              <p className="px-1 text-[11px] font-bold uppercase tracking-[0.09em] text-muted-foreground">
                {pt.pots.sectionEyebrow}
              </p>
              <div className="mt-1 flex flex-col gap-1">
                {POT_PURPOSE_ORDER.map((purpose) => (
                  <PotPurposeGroup
                    key={purpose}
                    purpose={purpose}
                    pots={groupedPots[purpose]}
                  />
                ))}
              </div>
            </div>

            <PotsFooterActions
              onDeposit={handleDeposit}
              onMove={handleMove}
              moveDisabled={activePots.length < 2}
            />

            {closedPots.length ? (
              <section className="mt-1 border-t border-border pt-2">
                <button
                  type="button"
                  onClick={() => setClosedOpen((open) => !open)}
                  className="flex w-full items-center justify-between px-1 py-2 text-left"
                >
                  <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    {pt.pots.closedSection}
                  </span>
                  <CaretDown
                    className={cn(
                      'h-4 w-4 text-muted-foreground transition-transform',
                      closedOpen && 'rotate-180',
                    )}
                    aria-hidden
                  />
                </button>
                {closedOpen ? <PotSection pots={closedPots} /> : null}
              </section>
            ) : null}
          </>
        )}
      </PageContentShell>

      <MovePotSheet
        isOpen={moveOpen}
        onClose={() => setMoveOpen(false)}
        pots={activePots}
      />
    </div>
  );
}

export default Pots;
